// Real Bridge.js server for live PLC data
const resolveBridgeWsUrl = () => {
  const env = ((import.meta as any)?.env || {}) as Record<string, string | undefined>;
  const configured = env.VITE_BRIDGE_WS_URL;
  if (configured && configured.trim()) {
    return configured.trim();
  }

  const host = env.VITE_BRIDGE_HOST?.trim()
    || window.location.hostname
    || '127.0.0.1';

  const port = env.VITE_BRIDGE_PORT?.trim() || '8081';
  const protocol = env.VITE_BRIDGE_PROTOCOL?.trim()
    || (window.location.protocol === 'https:' ? 'wss' : 'ws');

  return `${protocol}://${host}:${port}`;
};

const BRIDGE_WS_URL = resolveBridgeWsUrl();
const shouldUseServerWsHealthGate = () => {
  if (typeof window === 'undefined' || !window.location) {
    return true;
  }

  try {
    const wsUrl = new URL(BRIDGE_WS_URL, window.location.href);
    const pageHost = (window.location.hostname || '').trim().toLowerCase();
    const wsHost = (wsUrl.hostname || '').trim().toLowerCase();

    // VPS health should only gate websocket checks when targeting the same host as the portal.
    // If bridge websocket points to another host (client/gateway), allow direct connection attempts.
    return Boolean(wsHost) && wsHost === pageHost;
  } catch {
    return true;
  }
};

const USE_SERVER_WS_HEALTH_GATE = shouldUseServerWsHealthGate();

let bridgeWsAvailabilityCache: boolean | null = null;
let bridgeWsAvailabilityPromise: Promise<boolean> | null = null;

export const getResolvedBridgeWsUrl = () => BRIDGE_WS_URL;

const resolveBridgeWsAvailability = async () => {
  if (bridgeWsAvailabilityCache !== null) {
    return Boolean(bridgeWsAvailabilityCache);
  }

  if (bridgeWsAvailabilityPromise) {
    return bridgeWsAvailabilityPromise;
  }

  bridgeWsAvailabilityPromise = (async () => {
    try {
      const response = await fetch('/api/health', { method: 'GET' });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload && typeof payload.bridgeWsEnabled === 'boolean') {
        bridgeWsAvailabilityCache = payload.bridgeWsEnabled;
        return Boolean(bridgeWsAvailabilityCache);
      }
    } catch {
      // If health probe fails, keep websocket enabled by default.
    }

    bridgeWsAvailabilityCache = true;
    return Boolean(bridgeWsAvailabilityCache);
  })().finally(() => {
    bridgeWsAvailabilityPromise = null;
  });

  return bridgeWsAvailabilityPromise;
};

const DEFAULT_POLLING_INTERVAL_MS = 1000;
const MIN_POLLING_INTERVAL_MS = 1000;

type WebSocketClientOptions = {
  clientId?: string;
  pollingIntervalMs?: number;
};

const normalizePollingIntervalMs = (value?: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_POLLING_INTERVAL_MS;
  }

  return Math.max(MIN_POLLING_INTERVAL_MS, Math.round(parsed));
};

export class WebSocketClient {
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private handlers: Map<string, Set<(data: any) => void>> = new Map();
  private sessionToken: string;
  private clientId: string = '';
  private pollingIntervalMs: number = DEFAULT_POLLING_INTERVAL_MS;
  private isSocketConnected = false;
  private isAuthenticated = false;
  private liveDataInterval: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private ws: WebSocket | null = null;
  private sseSource: EventSource | null = null;
  private wsDisabledByServer = false;
  private pendingMessages: any[] = [];
  private maxPendingMessages = 200;

  constructor(sessionToken: string, clientId?: string, pollingIntervalMs?: number) {
    this.sessionToken = sessionToken;
    this.clientId = clientId || '';
    this.pollingIntervalMs = normalizePollingIntervalMs(pollingIntervalMs);
  }

  updateConnectionContext(sessionToken: string, clientId?: string, pollingIntervalMs?: number) {
    const nextClientId = clientId || this.clientId || '';
    const nextPollingIntervalMs = normalizePollingIntervalMs(pollingIntervalMs ?? this.pollingIntervalMs);
    const clientIdChanged = this.clientId !== nextClientId;
    const hasChanged = this.sessionToken !== sessionToken
      || clientIdChanged
      || this.pollingIntervalMs !== nextPollingIntervalMs;

    this.sessionToken = sessionToken;
    this.clientId = nextClientId;
    this.pollingIntervalMs = nextPollingIntervalMs;

    // The SSE relay (VPS mode) binds to one clientId in its URL when it first opens
    // and connectToPortalSse() no-ops while a connection already exists — so without
    // this, switching clients within the same browser tab/session (this client is a
    // module-level singleton) left the OLD client's SSE stream open forever, and this
    // browser kept receiving the previous client's live PLC events indefinitely. Tear
    // it down and reopen against the new clientId whenever it actually changes.
    if (clientIdChanged && this.sseSource) {
      this.sseSource.close();
      this.sseSource = null;
      this.isSocketConnected = false;
      this.isAuthenticated = false;
      this.connectToPortalSse();
    }

    if (!hasChanged || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      this.ws.send(JSON.stringify({
        type: 'authenticate',
        token: this.sessionToken,
        clientId: this.clientId,
        pollingIntervalMs: this.pollingIntervalMs,
        bridgeApiKey: (((import.meta as any)?.env || {}) as Record<string, string | undefined>)?.VITE_BRIDGE_API_KEY || ''
      }));
    } catch (error) {
      console.error('Failed to refresh WebSocket authentication context:', error);
    }
  }

  private emit(eventType: string, data: any) {
    const eventHandlers = this.handlers.get(eventType);
    console.log(`🔊 Emitting event "${eventType}" with ${eventHandlers?.size || 0} handlers`);
    if (!eventHandlers || eventHandlers.size === 0) {
      console.warn(`⚠️ No handlers registered for event "${eventType}"`);
      return;
    }

    eventHandlers.forEach((handler) => {
      try {
        console.log(`  ✅ Calling handler for "${eventType}"`);
        handler(data);
      } catch (error) {
        console.error(`Error in "${eventType}" handler:`, error);
      }
    });
  }

  connect() {
    if (this.isConnecting || this.isSocketConnected) {
      console.log('✅ Already connected to WebSocket');
      return;
    }

    if (this.wsDisabledByServer) {
      // VPS mode: use SSE instead of bridge WebSocket
      if (!this.sseSource && !this.isSocketConnected) {
        this.connectToPortalSse();
      }
      return;
    }

    if (!USE_SERVER_WS_HEALTH_GATE) {
      // External bridge host configured (client/gateway). Skip VPS health gating.
      this.connectToRealBridge();
      return;
    }

    this.isConnecting = true;
    resolveBridgeWsAvailability()
      .then((isEnabled) => {
        if (!isEnabled) {
          this.isConnecting = false;
          this.isAuthenticated = false;
          this.isSocketConnected = false;
          this.wsDisabledByServer = true;
          this.reconnectAttempts = this.maxReconnectAttempts;
          const errorHandlers = this.handlers.get('error');
          if (errorHandlers && errorHandlers.size > 0) {
            this.emit('error', {
              type: 'error',
              error: 'Bridge realtime socket is disabled on this server host',
              code: 'BRIDGE_WS_DISABLED',
            });
          }
          // VPS mode: fall back to portal SSE relay instead of direct bridge WebSocket
          this.connectToPortalSse();
          return;
        }

        // Connect to real bridge server for live PLC data
        this.connectToRealBridge();
      })
      .catch(() => {
        // Fallback to prior behavior if probe fails.
        this.connectToRealBridge();
      });
  }

  private connectToPortalSse() {
    if (!this.clientId) return;
    if (this.sseSource) return; // already connected

    const sseUrl = `/api/clients/${this.clientId}/live-stream`;
    console.log(`📡 Connecting to portal SSE relay at ${sseUrl}...`);

    const source = new EventSource(sseUrl);
    this.sseSource = source;

    source.onopen = () => {
      console.log('✅ Portal SSE relay connected');
      this.isSocketConnected = true;
      this.isAuthenticated = true;
      this.isConnecting = false;
      this.emit('connected', { type: 'connected', mode: 'sse' });
    };

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.type) this.emit(data.type, data);
      } catch (_) { /* ignore malformed events */ }
    };

    source.onerror = () => {
      console.warn('⚠️ Portal SSE relay error — will retry');
      this.isSocketConnected = false;
      this.isAuthenticated = false;
      this.sseSource = null;
      source.close();
      this.emit('disconnected', { type: 'disconnected' });
      // Retry after 5 seconds
      setTimeout(() => {
        if (!this.sseSource) this.connectToPortalSse();
      }, 5000);
    };
  }

  private connectToRealBridge() {
    try {
      this.isConnecting = true;
      this.isAuthenticated = false;
      console.log(`🔌 Connecting to Bridge.js server at ${BRIDGE_WS_URL}...`);

      this.ws = new WebSocket(BRIDGE_WS_URL);

      this.ws.onopen = () => {
        console.log('✅ Connected to Bridge.js server, sending auth handshake...');
        this.isSocketConnected = true;

        // Send authentication handshake immediately
        const authMessage = {
          type: 'authenticate',
          token: this.sessionToken,
          clientId: this.clientId,
          pollingIntervalMs: this.pollingIntervalMs,
          bridgeApiKey: (((import.meta as any)?.env || {}) as Record<string, string | undefined>)?.VITE_BRIDGE_API_KEY || ''
        };

        try {
          this.ws?.send(JSON.stringify(authMessage));
          console.log('📤 Sent authentication handshake');
        } catch (error) {
          console.error('Failed to send auth handshake:', error);
          this.ws?.close();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📩 Received from bridge:', data.type);

          // Handle authentication response
          if (data.type === 'bridge_authenticated') {
            this.isAuthenticated = true;
            this.clientId = data.clientId || this.clientId;
            this.pollingIntervalMs = normalizePollingIntervalMs(data.pollingIntervalMs ?? this.pollingIntervalMs);
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            console.log(`✅ Bridge authenticated - client: ${this.clientId}, interval: ${this.pollingIntervalMs}ms`);
            this.flushPendingMessages();
            this.emit('authenticated', data);
            this.emit('connected', data);
            this.getStatus();
            return;
          }

          // Authentication error
          if (data.type === 'auth_error') {
            console.error('❌ Authentication failed:', data.message);
            this.isAuthenticated = false;
            this.pendingMessages = [];
            this.emit('auth_error', data);
            this.ws?.close();
            return;
          }

          // Route authenticated messages
          if (this.isAuthenticated) {
            console.log(`✅ Client authenticated, emitting event: ${data.type}`);
            this.emit(data.type, data);
            this.emit('message', data);
          } else {
            console.warn(`⚠️ Client NOT authenticated! Received message type "${data.type}" but cannot emit - isAuthenticated = ${this.isAuthenticated}`);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ Bridge.js WebSocket error:', error);
        this.emit('error', { type: 'error', error: 'Connection failed' });
      };

      this.ws.onclose = () => {
        console.log('🔌 Disconnected from Bridge.js server');
        this.isSocketConnected = false;
        this.isAuthenticated = false;
        this.isConnecting = false;
        this.ws = null;

        this.emit('disconnected', { type: 'disconnected' });

        // Attempt reconnection only if intentional disconnect not called
        if (!this.wsDisabledByServer && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
          setTimeout(() => {
            this.isConnecting = false;
            this.connect();
          }, this.reconnectDelay);
        }
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('error', { type: 'error', error: 'Bridge server not available' });
    }
  }

  // New method: Subscribe to multiple PLCs at once
  connectMultiplePLCs(plcConfigs: any[]) {
    console.log(`🔌 Requesting to connect ${plcConfigs.length} PLCs`);
    for (const plcConfig of plcConfigs) {
      if ((plcConfig.tags && plcConfig.tags.length > 0) || plcConfig.protocol === 'energy-meter' || plcConfig.connectionType === 'energy-meter' || plcConfig.protocol === 's7' || plcConfig.connectionType === 's7') {
        this.connectPLC(plcConfig);
      } else {
        console.warn(`⚠️ Skipping PLC "${plcConfig.name}" - no tags configured`);
      }
    }
  }

  // Get all PLCs for current client and connect them
  async subscribeToClientPLCs(clientId: string, apiClient: any) {
    try {
      console.log(`📡 Subscribing to all PLCs for client: ${clientId}`);
      const plcs = await apiClient.getPLCs(this.sessionToken, clientId);
      
      if (!plcs || plcs.length === 0) {
        console.log('⚠️ No PLCs found for this client');
        return;
      }

      console.log(`✅ Found ${plcs.length} PLCs for client`);
      this.connectMultiplePLCs(plcs);
    } catch (error) {
      console.error('Failed to subscribe to client PLCs:', error);
    }
  }

  disconnect() {
    this.isSocketConnected = false;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.sseSource) {
      this.sseSource.close();
      this.sseSource = null;
    }
    
    if (this.liveDataInterval) {
      clearInterval(this.liveDataInterval);
      this.liveDataInterval = null;
    }

    console.log('🔌 WebSocket disconnected');
  }

  send(data: any) {
    const wsOpen = this.ws?.readyState === WebSocket.OPEN;
    console.log(`📤 Attempting to send message type: ${data.type}, isAuthenticated: ${this.isAuthenticated}, wsOpen: ${wsOpen}`);

    if (this.wsDisabledByServer) {
      // VPS mode — bridge WS intentionally disabled, SSE is used instead. Not an error.
      return false;
    }

    const message = { ...data, clientId: this.clientId };

    if (this.ws && wsOpen && this.isAuthenticated) {
      this.ws.send(JSON.stringify(message));
      console.log('📤 Sent to bridge:', data.type);
      return true;
    }

    if (this.pendingMessages.length >= this.maxPendingMessages) {
      this.pendingMessages.shift();
    }
    this.pendingMessages.push(message);
    console.warn(`⏳ WebSocket not ready; queued message type: ${data.type}. Queue size: ${this.pendingMessages.length}`);

    if (!this.isConnecting && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
      this.connect();
    }

    return true;
  }

  private flushPendingMessages() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isAuthenticated || this.pendingMessages.length === 0) {
      return;
    }

    const queued = [...this.pendingMessages];
    this.pendingMessages = [];
    for (const message of queued) {
      try {
        this.ws.send(JSON.stringify(message));
        console.log('📤 Flushed queued message to bridge:', message.type);
      } catch (error) {
        console.error('Failed to flush queued WebSocket message:', error);
      }
    }
  }

  connectPLC(plcConfig: any) {
    console.log('🔌 Requesting to connect PLC:', plcConfig.name);
    return this.send({
      type: 'connect_plc',
      config: plcConfig
    });
  }

  disconnectPLC(plcId: string) {
    console.log('🔌 Requesting to disconnect PLC:', plcId);
    return this.send({
      type: 'disconnect_plc',
      plcId
    });
  }

  getStatus() {
    console.log('📊 Requesting bridge status');
    return this.send({
      type: 'get_status'
    });
  }

  testConnection(plcConfig: any): Promise<any> {
    return new Promise((resolve) => {
      console.log('🧪 Starting test connection for:', plcConfig.name);

      const resolveWsDisabled = () => {
        resolve({
          success: false,
          message: '❌ Realtime bridge socket is disabled on this server host',
          details: 'Bridge service is disabled in VPS mode. Run bridge on gateway/client machine and use Bridge Connectivity test.',
        });
      };

      if (this.wsDisabledByServer) {
        resolveWsDisabled();
        return;
      }
      
      // Helper function to send the test message
      const attemptTest = () => {
        console.log('📤 Sending test_connection message...');
        
        const timeoutId = setTimeout(() => {
          console.log('⏱️ Test connection timeout after 15 seconds');
          this.off('plc_test_result', testResultHandler);
          resolve({
            success: false,
            message: '❌ Connection timeout - PLC not responding',
            details: 'The test exceeded 15 seconds. Check if PLC is powered on and reachable.'
          });
        }, 15000);

        // Register listener for test result
        const testResultHandler = (data: any) => {
          if (data.type === 'plc_test_result') {
            clearTimeout(timeoutId);
            this.off('plc_test_result', testResultHandler);
            console.log('✅ Test result received:', data);
            
            const success = data.connected ?? data.success ?? false;
            resolve({
              success,
              message: success
                ? `✅ Connection successful - PLC "${plcConfig.name}" is online and responding`
                : `❌ Connection failed - ${data.error || 'PLC not responding'}`,
              details: data.details || '',
              timestamp: data.timestamp || new Date().toISOString()
            });
          }
        };

        // Register the handler
        this.on('plc_test_result', testResultHandler);

        // Send the test message
        const sent = this.send({
          type: 'test_connection',
          config: plcConfig
        });

        if (!sent) {
          clearTimeout(timeoutId);
          this.off('plc_test_result', testResultHandler);
          resolve({
            success: false,
            message: '❌ Failed to send test message',
            details: 'WebSocket is not connected'
          });
        }
      };

      // Ensure connected to bridge
      const beginTest = async () => {
        if (USE_SERVER_WS_HEALTH_GATE) {
          try {
            const wsEnabled = await resolveBridgeWsAvailability();
            if (!wsEnabled) {
              this.wsDisabledByServer = true;
              resolveWsDisabled();
              return;
            }
          } catch {
            // Keep legacy behavior if probe fails.
          }
        }

        if (!this.isSocketConnected) {
          console.log('📡 Not connected - connecting to bridge...');
          this.connect();

          // Wait for connection to establish, then proceed
          let attempts = 0;
          const checkConnection = setInterval(() => {
            attempts++;

            if (this.wsDisabledByServer) {
              clearInterval(checkConnection);
              resolveWsDisabled();
              return;
            }

            if (this.isSocketConnected) {
              clearInterval(checkConnection);
              console.log('✅ Connected to bridge, now testing...');
              attemptTest();
            } else if (attempts > 50) { // 5 seconds max (50 * 100ms)
              clearInterval(checkConnection);
              resolve({
                success: false,
                message: '❌ Bridge websocket is not reachable from this host',
                details: 'Use Test Bridge Connectivity in Clients tab to validate gateway/client bridge ingestion path.',
              });
            }
          }, 100);
        } else {
          // Already connected
          console.log('✅ Already connected to bridge');
          attemptTest();
        }
      };

      beginTest();
    });
  }

  subscribe(plcIds?: string[]) {
    console.log('📡 Subscribing to PLCs:', plcIds || 'all');
    // WebSocket is real-time connected
    return true;
  }

  unsubscribe() {
    console.log('📡 Unsubscribing from PLCs');
    return true;
  }

  ping() {
    if (this.isSocketConnected) {
      console.log('🏓 WebSocket ping');
      return true;
    }
    return false;
  }

  on(eventType: string, handler: (data: any) => void) {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);
    console.log(`📋 Registered handler for event: ${eventType}`);
    return () => this.off(eventType, handler);
  }

  off(eventType: string, handler?: (data: any) => void) {
    if (!handler) {
      this.handlers.delete(eventType);
      console.log(`📋 Unregistered all handlers for event: ${eventType}`);
      return;
    }

    const eventHandlers = this.handlers.get(eventType);
    if (!eventHandlers) {
      return;
    }

    eventHandlers.delete(handler);
    if (eventHandlers.size === 0) {
      this.handlers.delete(eventType);
    }
    console.log(`📋 Unregistered handler for event: ${eventType}`);
  }

  isConnected() {
    // Must be both connected AND authenticated to send messages
    return this.isSocketConnected && this.isAuthenticated;
  }

  getReadyState() {
    if (this.isSocketConnected && this.isAuthenticated) return 'OPEN';
    if (this.isConnecting) return 'CONNECTING';
    return 'CLOSED';
  }

  async canUseRealtimeControl() {
    if (this.wsDisabledByServer) {
      return false;
    }

    if (!USE_SERVER_WS_HEALTH_GATE) {
      return true;
    }

    try {
      const wsEnabled = await resolveBridgeWsAvailability();
      this.wsDisabledByServer = !wsEnabled;
      return wsEnabled;
    } catch {
      return true;
    }
  }
}

// Create a singleton instance that can be reused
let wsClient: WebSocketClient | null = null;

export const getWebSocketClient = (sessionToken: string, options: WebSocketClientOptions = {}): WebSocketClient => {
  if (!wsClient) {
    wsClient = new WebSocketClient(sessionToken, options.clientId, options.pollingIntervalMs);
  } else {
    wsClient.updateConnectionContext(sessionToken, options.clientId, options.pollingIntervalMs);
  }
  return wsClient;
};

export const closeWebSocketClient = () => {
  if (wsClient) {
    wsClient.disconnect();
    wsClient = null;
  }
};
